import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import ConversationModal from '../../components/ConversationModal';
import { groupMessagesIntoSessions, sessionNoun } from '../../utils/conversationSessions';

const Conversations = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [blockedFilter, setBlockedFilter] = useState('');
    const [brandFilter, setBrandFilter] = useState('');

    // Brands list for filter (super admin only)
    const [brands, setBrands] = useState([]);

    useEffect(() => {
        fetchConversations();
        fetchBrands();
    }, [page, searchTerm, statusFilter, typeFilter, blockedFilter, brandFilter]);

    const fetchConversations = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();

            const params = new URLSearchParams({
                page: page.toString(),
                limit: '20',
            });

            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter) params.append('status', statusFilter);
            if (typeFilter) params.append('identifierType', typeFilter);
            if (blockedFilter) params.append('isBlocked', blockedFilter);
            if (brandFilter) params.append('brandId', brandFilter);

            const response = await axios.get(
                `/api/admin/conversations?${params.toString()}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setConversations(response.data.conversations);
            setTotalPages(response.data.pagination.pages);
            setTotal(response.data.pagination.total);
            setError(null);
        } catch (err) {
            console.error('Error fetching conversations:', err);
            setError(err.response?.data?.message || 'Failed to load conversations');
        } finally {
            setLoading(false);
        }
    };

    const fetchBrands = async () => {
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get('/api/admin/brands', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBrands(response.data.brands || []);
        } catch (err) {
            console.error('Error fetching brands:', err);
        }
    };

    const handleViewConversation = async (conversationId) => {
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get(`/api/admin/conversations/${conversationId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSelectedConversation(response.data.conversation);
            setShowModal(true);
        } catch (err) {
            console.error('Error fetching conversation:', err);
            alert('Failed to load conversation details');
        }
    };

    const handleBlockToggle = async (conversationId, isBlocked, reason = '') => {
        try {
            const token = await getAccessTokenSilently();
            await axios.patch(
                `/api/admin/conversations/${conversationId}/block`,
                { isBlocked, blockedReason: reason },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Refresh conversations
            fetchConversations();

            // Update modal if open
            if (selectedConversation?._id === conversationId) {
                setSelectedConversation(prev => ({
                    ...prev,
                    isBlocked,
                    blockedReason: reason,
                }));
            }
        } catch (err) {
            console.error('Error toggling block status:', err);
            alert('Failed to update conversation');
        }
    };

    const handleSendSms = async (conversationId, message) => {
        // No try/catch: let errors propagate so the modal can surface them
        const token = await getAccessTokenSilently();
        const response = await axios.post(
            `/api/admin/conversations/${conversationId}/send-sms`,
            { message },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        // Append the sent message to the open transcript
        const sentMessage = response.data.sentMessage;
        if (sentMessage && selectedConversation?._id === conversationId) {
            setSelectedConversation(prev => ({
                ...prev,
                messages: [...(prev.messages || []), sentMessage],
                lastMessageAt: sentMessage.timestamp || prev.lastMessageAt,
            }));
        }

        fetchConversations();
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'badge bg-success',
            resolved: 'badge bg-secondary',
            archived: 'badge bg-dark',
        };
        return badges[status] || 'badge bg-secondary';
    };

    return (
        <div className="container-fluid py-4">
            <div className="row mb-4">
                <div className="col">
                    <h2>Conversations</h2>
                    <p className="text-muted">
                        Manage all customer conversations across your brands
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by phone/GUID..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(1);
                                }}
                            />
                        </div>

                        {brands.length > 1 && (
                            <div className="col-md-2">
                                <select
                                    className="form-select"
                                    value={brandFilter}
                                    onChange={(e) => {
                                        setBrandFilter(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="">All Brands</option>
                                    {brands.map(brand => (
                                        <option key={brand._id} value={brand._id}>
                                            {brand.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="resolved">Resolved</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>

                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={typeFilter}
                                onChange={(e) => {
                                    setTypeFilter(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All Types</option>
                                <option value="sms">SMS</option>
                                <option value="voice">Voice</option>
                                <option value="web">Web</option>
                            </select>
                        </div>

                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={blockedFilter}
                                onChange={(e) => {
                                    setBlockedFilter(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All</option>
                                <option value="false">Not Blocked</option>
                                <option value="true">Blocked</option>
                            </select>
                        </div>

                        <div className="col-md-1">
                            <button
                                className="btn btn-primary w-100"
                                onClick={() => {
                                    setPage(1);
                                    fetchConversations();
                                }}
                            >
                                <i className="bi bi-arrow-clockwise"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Results */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : conversations.length === 0 ? (
                <div className="alert alert-info">No conversations found</div>
            ) : (
                <>
                    <div className="card">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>Identifier</th>
                                            {brands.length > 1 && <th>Brand</th>}
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Activity</th>
                                            <th>Last Activity</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {conversations.map(conv => (
                                            <tr key={conv._id}>
                                                <td>
                                                    {conv.identifier}
                                                    {conv.isBlocked && (
                                                        <span className="badge bg-danger ms-2">Blocked</span>
                                                    )}
                                                </td>
                                                {brands.length > 1 && (
                                                    <td>
                                                        {conv.brandId?.name || 'Unknown'}
                                                        <br />
                                                        <small className="text-muted">
                                                            {conv.brandId?.orgId}
                                                        </small>
                                                    </td>
                                                )}
                                                <td>
                                                    <span className={`badge ${conv.identifierType === 'sms' ? 'bg-info' : conv.identifierType === 'voice' ? 'bg-success' : 'bg-primary'}`}>
                                                        {conv.identifierType.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={getStatusBadge(conv.status)}>
                                                        {conv.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const messageCount = conv.messageCount || conv.messages?.length || 0;
                                                        const sessionCount = groupMessagesIntoSessions(conv.messages).length;
                                                        const noun = sessionNoun(conv.identifierType);
                                                        return (
                                                            <>
                                                                {sessionCount} {noun}{sessionCount === 1 ? '' : 's'}
                                                                <br />
                                                                <small className="text-muted">{messageCount} msg{messageCount === 1 ? '' : 's'}</small>
                                                            </>
                                                        );
                                                    })()}
                                                </td>
                                                <td>{formatDate(conv.lastMessageAt)}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleViewConversation(conv._id)}
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="text-muted">
                            Showing {conversations.length} of {total} conversations
                        </div>
                        <nav>
                            <ul className="pagination mb-0">
                                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    const pageNum = i + 1;
                                    return (
                                        <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        </li>
                                    );
                                })}

                                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </>
            )}

            {/* Conversation Modal */}
            {showModal && selectedConversation && (
                <ConversationModal
                    conversation={selectedConversation}
                    onClose={() => {
                        setShowModal(false);
                        setSelectedConversation(null);
                    }}
                    onBlockToggle={handleBlockToggle}
                    onSendSms={handleSendSms}
                />
            )}
        </div>
    );
};

export default Conversations;
